import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo, { localBusinessSchema, breadcrumbSchema } from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import CtaBand from "../components/CtaBand";
import { useServices, useSettings } from "../lib/hooks";

export default function Services() {
  const { data: services, isLoading } = useServices();
  const { data: settings } = useSettings();

  return (
    <div data-testid="services-page">
      <Seo
        title="Our Services | NOOR Design & Build Bristol"
        description="Premium extensions, renovations, loft and garage conversions, new builds, structural alterations, kitchens and bathrooms — one accountable team."
        path="/services"
        schema={{ "@context": "https://schema.org", "@graph": [localBusinessSchema(settings), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }])] }}
      />
      <section className="bg-graphite text-white blueprint-grid">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-16 md:pb-24">
          <Overline light>[ Services ]</Overline>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 max-w-3xl leading-[1.05]">
            Everything your home project needs, under one roof.
          </h1>
          <p className="text-white/60 text-base md:text-lg leading-relaxed mt-8 max-w-2xl">
            From a single extension to a complete renovation or a new-build home, every NOOR project is managed
            end to end by one accountable team — design, consents, construction and finishing.
          </p>
        </div>
      </section>

      <section className="bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-28">
          {isLoading && <p className="text-graphite/50 text-sm">Loading services…</p>}
          <div className="border-t border-black/10">
            {(services || []).map((s, i) => (
              <Reveal key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`services-row-${s.slug}`}
                  className="group grid md:grid-cols-12 gap-4 md:gap-8 items-center py-8 md:py-12 border-b border-black/10 hover:bg-stone/60 hover:translate-x-1 transition-[background-color,transform] duration-300 px-2 md:px-4 -mx-2 md:-mx-4"
                >
                  <span className="md:col-span-1 font-mono text-xs text-ember">[ {String(i + 1).padStart(2, "0")} ]</span>
                  <h2 className="md:col-span-4 font-display font-light text-2xl md:text-3xl tracking-tight group-hover:text-ember transition-colors duration-300">
                    {s.title}
                  </h2>
                  <p className="md:col-span-6 text-sm text-graphite/55 leading-relaxed">{s.summary}</p>
                  <span className="md:col-span-1 justify-self-start md:justify-self-end">
                    <ArrowUpRight size={20} strokeWidth={1.25} className="text-graphite/30 group-hover:text-ember transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <CtaBand />
    </div>
  );
}
